/**
 * WALL-HUNG TOILET (2026-10-21, Matt: "a new family … Wall Hung Toilet which takes up less space
 * and has a wall carrier with the tank in the wall. Look up one by Duravit and use its dimensions
 * and look … The plumbing hookups should accurately reflect how the toilet works").
 *
 * Its own object kind (`wallHungToilet`), not a style of Toilet: the rough-in is a different
 * trade. A floor toilet needs a closet flange at 12" and a supply at the floor; this one needs a
 * steel CARRIER in the stud bay that holds a concealed TANK, the bowl's mounting rods and a waste
 * bend, with the flush plate on the finished wall. Every number below is the one rule the 3D
 * body, the plan symbol, the hookups and the Properties read.
 *
 * Measured from the maker's US documents (character names in the UI, never the brand — Matt):
 *  - BOWL — Duravit ME by Starck wall-mounted, rimless, 2529090092: 370 × 570 mm
 *    (14-5/8" × 22-1/2"), ceramic body ~360 mm (14-1/8") tall, horizontal outlet, concealed
 *    "Durafix" fixing. The shape is read off Duravit's own top view and section: a D-plan
 *    (straight sides, a long front curve), a flat top, the front leaning back as it drops, a
 *    deck behind the seat with the hinges, and the trap's bulb just below the skirt.
 *  - CARRIER — Duravit DuraSystem in-wall tank carrier WD1022 (US): frame 590 × 1148 × 89 mm
 *    (23-1/4" × 45-1/4" × 3-1/2" — fits a 2x4 or a 2x6 wall), feet raise it 0-240 mm
 *    (0-9-1/2"); at the frame's base setting the waste centre is 220 mm (8-5/8") above the
 *    floor, the mounting rods 320 mm (12-5/8"), M12 at 180 mm (7-1/8") centres, the actuator
 *    centre 1000 mm (39-3/8"); the waste bend (DN 90) drops into the floor 90 mm (3-1/2")
 *    behind the frame's face; 1/2" supply into the top of the tank. With this bowl that base
 *    setting puts the rim at 400 mm (15-3/4"), the height the maker quotes.
 *  - FLUSH PLATE — Duravit A1 dual-flush actuator for that carrier, WD5008: 8-1/2" × 5-3/4".
 *
 * Frame of reference (objectKinds.ts): x across centred on 0, y = 0 the FINISHED wall face, +y
 * into the room, z up from the floor, all INCHES here. The carrier sits behind the wall face
 * (y < 0) with its front on the stud face, 5/8" behind the finished face (the board). Its frame fits
 * a 2x4 wall, but the 3" bend drops 3-1/2" behind the stud face, so the waste wants a 2x6 wall —
 * the drain hookup says so.
 */

export const WALL_HUNG = {
  // the bowl
  bowlW: 14.625,
  bowlD: 22.5,
  /** ceramic body height, top of rim to the bottom of the trap */
  bodyH: 14.125,
  /** the side skirt's drop below the rim (the trap bulb hangs below it to bodyH) */
  skirtH: 11.8,
  /** where the straight sides end and the front curve begins, from the wall */
  sideRunD: 13.4,
  /** the deck behind the seat — the lid starts here */
  seatBackY: 5.3,
  /** the seat ring and the closed lid, stacked on the rim */
  seatT: 0.55,
  lidT: 1.0,
  // rim height (top of the ceramic) — the carrier's feet set it
  rimStd: 15.75,
  rimMin: 15.75,
  /** 19" covers an ADA seat (17-19" to the top of the seat); the feet go further */
  rimMax: 19,
  // the bowl's connections, below the rim (fixed by the bowl, so they ride the rim)
  outletBelowRim: 7.125,   // 8-5/8" waste centre at the standard rim
  inletBelowRim: 2.4,      // the flush-pipe spigot, high on the bowl's back
  rodBelowRim: 3.125,      // 12-5/8" mounting rods at the standard rim
  rodSpacing: 7.125,       // 180 mm
  // the carrier, at its base setting (feet fully down)
  frameW: 23.25,
  frameH: 45.25,
  frameD: 3.5,
  /** the frame's face, behind the finished wall face */
  frameFrontY: -0.625,
  /** the waste bend's riser centre behind the frame's face (DN 90 drop into the floor) */
  wasteBehindFrame: 3.5,
  wasteOD: 3.5,             // DN 90
  flushPipeOD: 1.75,        // 45 mm flush pipe, tank to bowl
  tankZ0: 23.3,
  tankZ1: 41.9,
  supplyX: -2.9,
  supplyZ: 43.6,
  // the flush plate
  plateW: 8.5,
  plateH: 5.75,
  plateT: 0.4,
  plateZ: 39.375,
} as const

/** the rim height an instance is set to (inches), clamped to the carrier's range */
export function wallHungRimIn(params: Record<string, unknown> | undefined): number {
  const ft = Number(params?.wallHungRimFt)
  const rim = Number.isFinite(ft) && ft > 0 ? ft * 12 : WALL_HUNG.rimStd
  return Math.min(WALL_HUNG.rimMax, Math.max(WALL_HUNG.rimMin, rim))
}

/** how far the carrier's feet raise it off its base setting for a rim height (inches) */
export function wallHungRaiseIn(rimIn: number): number {
  return Math.max(0, rimIn - WALL_HUNG.rimStd)
}

/** the vertical extent of the whole fixture — carrier feet on the floor to the frame's top */
export function wallHungZSpanIn(rimIn: number): { z0: number; z1: number } {
  return { z0: 0, z1: WALL_HUNG.frameH + wallHungRaiseIn(rimIn) }
}

/** the rough-in points (inches) the hookups and the 3D share */
export function wallHungRoughIn(rimIn: number) {
  const up = wallHungRaiseIn(rimIn)
  return {
    rim: rimIn,
    outletZ: rimIn - WALL_HUNG.outletBelowRim,
    inletZ: rimIn - WALL_HUNG.inletBelowRim,
    rodZ: rimIn - WALL_HUNG.rodBelowRim,
    plateZ: WALL_HUNG.plateZ + up,
    supplyZ: WALL_HUNG.supplyZ + up,
    tankZ0: WALL_HUNG.tankZ0 + up,
    tankZ1: WALL_HUNG.tankZ1 + up,
    frameZ0: up,
    frameZ1: WALL_HUNG.frameH + up,
    /** the waste riser's centre, behind the finished wall face (negative y) */
    wasteY: WALL_HUNG.frameFrontY - WALL_HUNG.wasteBehindFrame,
  }
}
